import { useState } from 'react';
import { EyeOutlined, MoreOutlined, PoweroffOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Dropdown, Modal, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { RoleResponse } from '../../../api/types';
import { useT } from '../../../locales';

const ROLE_ACTION = {
  OPEN: 'open',
  DEACTIVATE: 'deactivate',
  REACTIVATE: 'reactivate',
} as const;

type RoleAction = (typeof ROLE_ACTION)[keyof typeof ROLE_ACTION];

export interface RoleActionsProps {
  role: RoleResponse;
  canRead: boolean;
  canUpdate: boolean;
  canDeactivate: boolean;
  loading?: boolean;
  onOpen: (role: RoleResponse) => void;
  onDeactivate: (role: RoleResponse) => void;
  onReactivate: (role: RoleResponse) => void;
}

export function RoleActions({
  role,
  canRead,
  canUpdate,
  canDeactivate,
  loading = false,
  onOpen,
  onDeactivate,
  onReactivate,
}: RoleActionsProps) {
  const t = useT();
  const [pendingAction, setPendingAction] = useState<RoleAction | null>(null);
  const canOpen = canRead || canUpdate;
  const menuItems: MenuProps['items'] = [];

  if (canOpen) {
    menuItems.push({
      key: ROLE_ACTION.OPEN,
      icon: canUpdate ? <SettingOutlined /> : <EyeOutlined />,
      label: canUpdate ? t('pages.roles.manage') : t('pages.roles.view'),
    });
  }

  if (canDeactivate && role.isActive) {
    menuItems.push({
      key: ROLE_ACTION.DEACTIVATE,
      icon: <PoweroffOutlined />,
      label: t('pages.roles.deactivate'),
    });
  }

  if (canUpdate && !role.isActive) {
    menuItems.push({
      key: ROLE_ACTION.REACTIVATE,
      icon: <PoweroffOutlined />,
      label: t('pages.roles.reactivate'),
    });
  }

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === ROLE_ACTION.OPEN) {
      onOpen(role);
      return;
    }

    if (key === ROLE_ACTION.DEACTIVATE || key === ROLE_ACTION.REACTIVATE) {
      setPendingAction(key);
    }
  };

  const handleConfirm = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action === ROLE_ACTION.DEACTIVATE) onDeactivate(role);
    if (action === ROLE_ACTION.REACTIVATE) onReactivate(role);
  };

  const isDeactivation = pendingAction === ROLE_ACTION.DEACTIVATE;

  return (
    <>
      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          aria-label={t('pages.roles.rowActions', { name: role.name })}
          title={t('pages.roles.rowActions', { name: role.name })}
          disabled={loading || menuItems.length === 0}
        />
      </Dropdown>

      <Modal
        open={pendingAction !== null}
        centered
        title={
          isDeactivation
            ? t('pages.roles.deactivateConfirmTitle')
            : t('pages.roles.reactivateConfirmTitle')
        }
        okText={isDeactivation ? t('pages.roles.deactivate') : t('pages.roles.reactivate')}
        cancelText={t('pages.roles.cancelAction')}
        onCancel={() => setPendingAction(null)}
        onOk={handleConfirm}
        confirmLoading={loading}
      >
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          {isDeactivation
            ? t('pages.roles.deactivateConfirmDescription', { name: role.name })
            : t('pages.roles.reactivateConfirmDescription', { name: role.name })}
        </Typography.Paragraph>
      </Modal>
    </>
  );
}
